<template>
  <div id="production-table">
    <div class="table-header d-flex justify-space-between align-center">
      <div class="filterToggles d-flex align-center">
        <v-switch
          color="primary"
          :label="includeDispatched ? 'Dispatched' : 'Awaiting Dispatch'"
          @update:model-value="includeDispatched = !includeDispatched"
          hide-details
          class="mr-3"
        ></v-switch>
      </div>
      <v-text-field
        v-model="tableSearch"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        :clearable="true"
      ></v-text-field>
    </div>
    <v-data-table
      :items="filteredAwaitingPaymentOrders"
      :headers="headers"
      :search="tableSearch"
      :loading="loading"
      :no-data-text="noDataText"
      :items-per-page="-1"
      hide-default-footer
      :sort-by="[{ key: 'dateCreated', order: 'desc' }]"
      must-sort
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>
            <div style="display: flex; align-items: center; justify-content: space-between">
              <div>
                {{
                  item.region !== 'us' && item.dateScheduled
                    ? $utils.momentFromTimestamp(item.dateScheduled).format('Do MMM YYYY')
                    : item.region !== 'us'
                    ? 'Not scheduled'
                    : '-'
                }}
              </div>
            </div>
          </td>
          <td>
            <div style="display: flex; align-items: center; justify-content: space-between">
              <div>
                {{
                  item.region === 'us' && item.dispatchEta
                    ? $utils.momentFromTimestamp(item.dispatchEta).format('Do MMM YYYY')
                    : item.region === 'us'
                    ? 'Not scheduled'
                    : '-'
                }}
              </div>
            </div>
          </td>
          <td>{{ item.orderNumber }}</td>
          <td>{{ item.customerContactDetails.email }}</td>
          <td>{{ item.creatorEmail || 'n/a' }}</td>
          <td>
            {{ orderCurrencySymbol(item.region) + $numeral(item.total).format('0,0.00') || '-' }}
          </td>
          <td class="d-flex align-center">
            <v-icon size="small" class="pr-2" v-if="item.bankTransfer">mdi-bank</v-icon>
            <v-icon size="small" class="pr-2" v-else>mdi-credit-card</v-icon>
            {{
              item.numPayments > 0
                ? 'Awaiting Balance' + ' (' + item.numPayments + ')'
                : 'Awaiting Initial' + ' (' + item.numPayments + ')'
            }}
          </td>
          <td>
            {{ orderCurrencySymbol(item.region) + $numeral(item.paymentsTotal).format('0,0.00') || '-' }}
          </td>
          <td>
            {{ orderCurrencySymbol(item.region) + $numeral(item.balanceDue).format('0,0.00') || '-' }}
          </td>
          <td class="text-right">
            <v-tooltip location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon size="small" class="mr-1" @click="viewQuote(item)" v-bind="props">mdi-clipboard-text</v-icon>
              </template>
              <span>Original Quote</span>
            </v-tooltip>
            <v-tooltip location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon size="small" class="ml-1 mr-1" @click="openPaymentTypeDialog(item)" v-bind="props"
                  >mdi-account-cash</v-icon
                >
              </template>
              <span>Set payment type</span>
            </v-tooltip>
            <v-tooltip location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon
                  size="small"
                  class="mr-1"
                  @click="
                    () => {
                      selectedOrder = item
                      addPaymentDialog = true
                      payment.date = $moment().toDate()
                      payment.value = null
                    }
                  "
                  v-bind="props"
                  >mdi-currency-usd</v-icon
                >
              </template>
              <span>Manually add payment</span>
            </v-tooltip>
            <v-tooltip v-if="$store.state.user.permissions.admin" location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon
                  size="small"
                  class="mr-1"
                  @click="
                    () => {
                      clearBalanceDialog = true
                      selectedOrder = item
                    }
                  "
                  v-bind="props"
                  >mdi-cancel</v-icon
                >
              </template>
              <span>Clear Balance</span>
            </v-tooltip>
            <v-tooltip v-if="$store.state.user.permissions.admin" location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon
                  size="small"
                  @click="
                    () => {
                      deleteOrderDialog = true
                      selectedOrder = item
                    }
                  "
                  v-bind="props"
                  >mdi-delete</v-icon
                >
              </template>
              <span>Delete Order</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- DIALOGS -->
    <!-- add payment dialog -->
    <v-dialog v-model="addPaymentDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Manually Add Payment</v-card-title>
        <v-card-text class="pt-6">
          <MinimalDatePicker v-model="payment.date"></MinimalDatePicker>
          <v-text-field
            ref="addPaymentInput"
            type="number"
            v-if="selectedOrder"
            class="mt-3"
            label="Amount"
            v-model="payment.amount"
            :prefix="orderCurrencySymbol(selectedOrder.region)"
            :rules="addPaymentRules"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="
              () => {
                ;(payment.amount = null), (payment.date = this.$moment().toDate()), (addPaymentDialog = false)
              }
            "
            >Cancel</v-btn
          >
          <v-btn color="primary" :disabled="!payment.amount" variant="flat" @click="addPayment(selectedOrder)"
            >Add Payment</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- clear balance dialog -->
    <v-dialog v-model="clearBalanceDialog" width="500">
      <v-card>
        <v-card-title class="bg-warning text-white text-h6">Clear Outstanding Balance</v-card-title>
        <v-card-text class="pt-6 font-weight-bold"
          >Are you sure you want to CLEAR any balance outstanding for this order?</v-card-text
        >
        <v-card-text class="pt-6"
          >Customer Email: <span>{{ selectedOrder.customerEmail }}</span></v-card-text
        >
        <v-card-text class="pt-2"
          >Order Total:
          <span>{{
            orderCurrencySymbol(selectedOrder.region) + $numeral(selectedOrder.total).format('0,0.00') || '-'
          }}</span></v-card-text
        >
        <v-card-text class="pt-2"
          >Payment Total:
          <span>{{
            orderCurrencySymbol(selectedOrder.region) + $numeral(selectedOrder.paymentsTotal).format('0,0.00') || '-'
          }}</span></v-card-text
        >
        <v-divider class="mx-5"></v-divider>
        <v-card-text class="py-2"
          >Outstanding Balance Due:
          <span class="text-red">{{
            orderCurrencySymbol(selectedOrder.region) + $numeral(selectedOrder.balanceDue).format('0,0.00') || '-'
          }}</span></v-card-text
        >
        <v-divider class="mx-5"></v-divider>
        <v-card-actions class="pt-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="clearBalanceDialog = false">Cancel</v-btn>
          <v-btn variant="flat" color="warning" @click="clearBalance(selectedOrder)">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- delete order dialog -->
    <v-dialog v-model="deleteOrderDialog" width="500">
      <v-card>
        <v-card-title class="bg-warning text-white text-h6">Delete Order</v-card-title>
        <v-card-text class="pt-6">Are you sure you want to delete this order? </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="deleteOrderDialog = false">Cancel</v-btn>
          <v-btn variant="flat" color="warning" @click="deleteOrder(selectedOrder)">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Component Overlay Dialog -->
    <v-dialog v-model="dialogVisible" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar color="primary" class="no-print elevation-0">
          <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="
              () => {
                dialogVisible = false
                overlayComponent = null
              }
            "
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <component
          :is="overlayComponent"
          :order="selectedOrder"
          :quoteID="selectedOrder.quoteID"
          :versionID="selectedOrder.versionID"
        ></component>
        <v-card-text> </v-card-text>
      </v-card>
    </v-dialog>

    <SetPaymentTypeDialog
      v-model="paymentTypeDialog"
      :order="selectedOrder.id ? selectedOrder : null"
      @saved="onPaymentTypeSaved"
    />
  </div>
</template>

<script>
import QuoteEdit from '@/views/QuoteEdit'
import SetPaymentTypeDialog from '@/components/SetPaymentTypeDialog'
import MinimalDatePicker from '@/components/ui/MinimalDatePicker'
import { db, functions } from '@/js/db'
import { collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

export default {
  name: 'AwaitingPayment',
  components: {
    QuoteEdit,
    SetPaymentTypeDialog,
    MinimalDatePicker
  },
  data() {
    return {
      loading: true,
      awaitingPaymentOrders: [],
      selectedOrder: {},
      overlayComponent: null,
      dialogTitle: null,
      dialogVisible: false,
      addPaymentDialog: false,
      paymentTypeDialog: false,
      clearBalanceDialog: false,
      deleteOrderDialog: false,
      includeDispatched: false,
      payment: {
        date: this.$moment().toDate(),
        amount: null
      },
      addPaymentRules: [(v) => !!v || 'Amount is required', (v) => (v && v > 0) || 'Amount must be greater than 0'],
      headers: [
        {
          title: 'Scheduled Date',
          sortable: true,
          key: 'dateScheduled'
        },
        {
          title: 'Dispatch ETA',
          sortable: true,
          key: 'dispatchETA'
        },
        {
          title: 'Order Number',
          sortable: false,
          key: 'orderNumber'
        },
        {
          title: 'Customer',
          sortable: false,
          key: 'customerEmail'
        },
        {
          title: 'Creator',
          sortable: false,
          key: 'creatorEmail'
        },
        {
          title: 'Order Total',
          sortable: false,
          key: 'total'
        },
        {
          title: 'Number of Payments',
          sortable: false,
          key: 'numPayments'
        },
        {
          title: 'Payments Total',
          sortable: false,
          key: 'paymentsTotal'
        },
        {
          title: 'Balance Due',
          sortable: false,
          key: 'balanceDue'
        },
        {
          title: 'Actions',
          sortable: false,
          align: 'end',
          key: null
        }
      ],
      tableSearch: ''
    }
  },
  created() {
    this.loadData()
  },
  computed: {
    noDataText() {
      return this.loading ? '' : 'Nothing to display'
    },
    filteredAwaitingPaymentOrders() {
      return this.awaitingPaymentOrders.filter((order) => {
        const dispatched = order.region === 'us' ? order?.shipped : order?.dispatched
        return this.includeDispatched ? dispatched === true : !dispatched
      })
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      this.awaitingPaymentOrders = []

      const orderSnap = await getDocs(
        query(collection(db, 'orders'), where('type', '==', 'mega-admin'), where('foc', '==', false))
      )

      for (const order of orderSnap.docs) {
        let orderObj = {}
        let orderData = order.data()
        let orderID = order.id

        // balanceCleared can be toggled to true to force clear awaiting payment status
        if (orderData?.orderNumber && orderData?.balanceOutstanding && orderData?.balanceCleared !== true) {
          let orderTotal = this.$utils.toFixedNumber(orderData.total, 2) // ensure it's a number
          // Calculate total of payments
          const paymentsTotal = this.$utils.calculatePaymentsTotal(orderData.payments)

          const balanceDue = this.$utils.calculateBalanceDue(orderTotal, orderData.payments)
          if (balanceDue > 0) {
            let numPayments = orderData.numPayments || 0

            if (orderData.region === 'us') {
              // US dispatchEta info is in order
              orderObj = {
                id: orderID,
                ...orderData,
                dispatchEta: orderObj?.dispatchEta,
                numPayments,
                paymentsTotal,
                balanceDue
              }
            } else {
              // UK and EU: dateScheduled info from shipment (EU ships via UK)
              let dateScheduled
              const shipmentsSnap = await getDocs(query(collection(db, 'shipments'), where('orderID', '==', orderID)))
              for (const shipment of shipmentsSnap.docs) {
                let shipmentData = shipment.data()
                if (shipmentData.lineItems.length === 1 && shipmentData.lineItems[0] === 'spacers') continue // skip shipment for early spacers
                if (shipmentData.lineItems.length === 1 && shipmentData.lineItems[0] === 'worktops') continue // skip shipment for early worktops
                if (shipmentData.dateScheduled) {
                  dateScheduled = shipmentData?.dateScheduled
                }
              }
              orderObj = {
                id: orderID,
                ...orderData,
                dateScheduled,
                numPayments,
                paymentsTotal,
                balanceDue
              }
            }
            this.awaitingPaymentOrders.push(orderObj)
          }
        }
      }
      this.loading = false
    },
    orderCurrencySymbol(region) {
      if (region === 'us') return '$'
      if (region === 'eu') return '€'
      return '£'
    },
    viewQuote(order) {
      this.dialogVisible = true
      this.selectedOrder = order
      this.dialogTitle = order.customerEmail
      this.overlayComponent = 'QuoteEdit'
    },
    async openPaymentTypeDialog(order) {
      try {
        const snap = await getDoc(doc(db, 'orders', order.id))
        if (snap.exists()) {
          const fresh = { id: order.id, ...snap.data() }
          const idx = this.awaitingPaymentOrders.findIndex((o) => o.id === order.id)
          if (idx !== -1) {
            this.awaitingPaymentOrders[idx] = { ...this.awaitingPaymentOrders[idx], ...fresh }
            this.selectedOrder = this.awaitingPaymentOrders[idx]
          } else {
            this.selectedOrder = fresh
          }
        } else {
          this.selectedOrder = order
        }
      } catch (e) {
        this.selectedOrder = order
      }
      this.paymentTypeDialog = true
    },
    onPaymentTypeSaved(method) {
      // keep the row in sync after save (list is loaded one-shot, not bound live)
      if (this.selectedOrder && this.selectedOrder.id) {
        this.selectedOrder.bankTransfer = method === 'bacs'
      }
    },
    async addPayment(order) {
      const isValid = await this.$utils.validateForm(this.$refs.addPaymentInput)
      if (!isValid) {
        return
      }

      order.payments.push({
        amount: parseFloat(this.payment.amount),
        date: this.$utils.timestampFromDate(this.payment.date),
        method: 'bacs'
      })

      let effectiveTotal = order.total
      if (order.region === 'us' && order.quoteID && order.versionID) {
        const quoteSnap = await getDoc(doc(db, 'quotes', order.quoteID))
        const version = quoteSnap.data()?.versions?.find((v) => v.id === order.versionID)
        if (version?.pricing?.total != null && version?.pricing?.salesTax?.amount_to_collect != null) {
          effectiveTotal = this.$utils.toFixedNumber(
            version.pricing.total + version.pricing.salesTax.amount_to_collect,
            2
          )
        }
      }

      await updateDoc(doc(db, 'orders', order.id), {
        numPayments: order.numPayments + 1,
        payments: order.payments,
        balanceOutstanding: this.$utils.calculateBalanceOutstanding(effectiveTotal, order.payments)
      })

      this.addPaymentDialog = false
      this.$store.commit('openSnackbar', {
        snackbarText: 'Payment added',
        timeout: 5000
      })
      this.selectedOrder = {}
      this.payment.amount = null
      this.payment.date = this.$moment().toDate()
      this.$refs.addPaymentInput.reset()
      this.loadData()
    },
    async clearBalance() {
      this.clearBalanceDialog = false

      await updateDoc(doc(db, 'orders', this.selectedOrder.id), {
        balanceOutstanding: false,
        balanceCleared: true // mark as cleared
      })

      this.$store.commit('openSnackbar', { snackbarText: 'Balance cleared', timeout: 5000 })

      this.selectedOrder = {}
      this.loadData()
    },
    async deleteOrder(order) {
      this.deleteOrderDialog = false
      const deleteOrderFunction = httpsCallable(functions, 'deleteOrder')
      await deleteOrderFunction({
        order: order,
        orderID: order.id
      })
      this.$store.commit('openSnackbar', {
        snackbarText: 'Order deleted',
        timeout: 5000
      })
    }
  }
}
</script>
